import { Selector } from "react-redux";

import { EMPTY_ARRAY } from "@multiweb/reactUtils";
import { IAppState } from "@multiweb/types/react";
import { EnumInstance, EnumKey, EnumValueSelectorParams, IReduxEnumDescription, IReduxEnumType } from "./types";
import { enumHas, getEnumKeys, getFromEnum } from "./utils";

/**
 * Získá z globálního stavu podstrom patřící enums modulu.
 */
const getModel = (state: IAppState) => state.core.enums;

/**
 * Získá podstrom stavu patřící konkrétnímu enumu.
 * @param state
 * @param enumsKey Klíč enumu sloužící k jeho jednoznačné identifikaci.
 */
export const getEnumsInstance: Selector<IAppState, EnumInstance | undefined, EnumKey> = (state, enumsKey) => getModel(state).enumsInstances[enumsKey];

/**
 * Získa na základě `enumTypeName` ze stavu enums modulu správný typ enumu. Pokud enum neexistuje, vrací undefined.
 * @param state stav enums modulu
 * @param enumTypeName indexový klíč, na jehož základě se vrací správný typ enumu
 */
export const getEnumType: Selector<IAppState, IReduxEnumType | null | undefined, string> = (state: IAppState, enumTypeName: string) => getEnumsInstance(state, enumTypeName)?.enumType;

/**
 * Získá vnitřní čítač pole.
 * @param state stav enums modulu
 * @param enumsKey Klíč pole sloužící k jeho jednoznačné identifikaci.
 */
export const getCounter: Selector<IAppState, number | undefined, EnumKey> = (state, enumsKey) => getEnumsInstance(state, enumsKey)?.counter ?? undefined;

/**
 * Vrací klíče enumů, které doposud nebyly inicializováný. Pokud enum inicializován již byl, ve výsledku jej vynechá.
 * @param state stav enums modulu
 * @param enumsKeys klíče enumů, které se prozkoumají kvůli zjištění inicializace
 */
export const getUninitializedEnums: Selector<IAppState, ReadonlyArray<EnumKey>, ReadonlyArray<EnumKey>> = (state, enumsKeys) => enumsKeys.filter(enumKey => !getEnumType(state, enumKey));

/**
 * Na základě jména enumu zjistí, zda již v reduxu existuje typ enumu.
 * @param state stav enums modulu
 * @param enumTypeName indexový klíč, na jehož základě se zjistí, zda enum type v reduxu již existuje
 */
export const hasEnumType: Selector<IAppState, boolean, string> = (state: IAppState, enumTypeName: string) => getEnumType(state, enumTypeName) !== undefined;

/**
 * Vrací description na základě parametru `value`. Pokud hodnota není nalezena, vrací `undefined`.
 * @param state stav enums modulu
 * @param enumParams parametry pro selektory k vyhledání hodnoty enumu.
 */
export const getValue: Selector<IAppState, IReduxEnumDescription | undefined, EnumValueSelectorParams> = (state: IAppState, enumParams: EnumValueSelectorParams) => {
    const { enumTypeName, value } = enumParams;

    const enumType = getEnumType(state, enumTypeName);
    if (!enumType) {
        return undefined;
    }

    return getFromEnum(value, enumType);
};

/**
 * Vrací, zda je mezi descriptionami pro zadané jméno obsažena konkrétní hodnota v některém enumu. Pokud některý enum hodnotu obsahuje, tak vrací `true`, jinak `false`.
 * @param state stav enums modulu
 * @param enumParams parametry pro selektory k vyhledání hodnoty enumu.
 */
export const hasValue: Selector<IAppState, boolean, EnumValueSelectorParams> = (state: IAppState, enumParams: EnumValueSelectorParams) => {
    const { enumTypeName, value } = enumParams;

    const enumType = getEnumType(state, enumTypeName);
    if (!enumType) {
        return false;
    }

    return enumHas(value, enumType);
};

/**
 * Vrací stringové identifikátory položek enumu pro zadané jméno typu enumu.
 * @param state stav enums modulu
 * @param enumTypeName klíč, na jehož základě se vrátí správný typ enumu
 */
export const getItemNames: Selector<IAppState, ReadonlyArray<string>, string> = (state: IAppState, enumTypeName: string) => {
    const enumType = getEnumType(state, enumTypeName);
    if (!enumType) {
        return EMPTY_ARRAY;
    }

    return getEnumKeys(enumType);
};
